import { useEffect, useCallback, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  isEmpty,
  find,
  isNumber,
} from 'lodash'
import {
  GET_EXTERNAL_ACCOUNTS,
  GET_CORPORATE_EXTERNAL_ACCOUNTS,
  GET_HRIS_ACCOUNTS,
  GET_HRIS_STATUS,
  GET_FIRST_SYNC_STATUS,
  GET_HRIS_STATUS_COUNTS,
  GET_HRIS_ELIGIBILITY,
  GET_CONTRIBUTION_SETTINGS,
  GET_PAYROLL_ACCESS_TOKEN,
  GET_COMPANY,
  GET_CORPORATE_EXTERNAL_CARD,
  GET_EXTERNAL_CARDS,
  REMOVE_CORPORATE_EXTERNAL_CARD,
  REMOVE_EXTERNAL_CARD,
} from 'api/constants'
import {
  DWOLLA_STATUS,
  DWOLLA_SUB_STATUS,
  PLAID_STATUS,
} from 'utils/constants'
import {
  clearFromState,
} from 'slices/globalSlice'
import { Atomic, Product } from '@atomicfi/transact-javascript'
import { 
  useGet, 
  useRoles, 
  useUniqueID, 
  useTypeLoad,
} from 'utils/hooks'
import { useAction } from 'utils/actions'
import RemoveCorporateExternalCard from 'components/ModalViews/RemoveCorporateExternalCard'

export const useExternalAccount = () => {
  const user = useSelector(({ global }) => global.user)
  const externalAccounts = useGet(GET_EXTERNAL_ACCOUNTS, 'externalAccounts', [])
  const [activeAccount, setActiveAccount] = useState(null)
  const primaryAccount = find(externalAccounts, { id: user.primary_connected_account_id }, {})

  return {
    primaryAccount,
    activeAccount: activeAccount || primaryAccount,
    setActiveAccount: data => setActiveAccount(isNumber(data) ? data : find(externalAccounts, { id: data }, {})),
    externalAccounts,
  }
}

export const usePrimaryHRISAccount = () => {
  const dispatch = useDispatch()
  const { isAdmin } = useRoles()
  const company = useGet(GET_COMPANY, 'company')
  const hrisEnabled = useMemo(() => !!company.enabled_features?.corp_hris, [company.enabled_features?.corp_hris])
  const hrisAccounts = useSelector(({ global }) => global.hrisAccounts)
  const account = useMemo(() => !isEmpty(hrisAccounts) && hrisEnabled ? hrisAccounts[0] : null, [hrisAccounts])
  const { status } = useSelector(({ global }) => global.hrisStatus)
  const firstSyncStatus = useSelector(({ global }) => global.firstSyncStatus)
  const hrisStatusCounts = useSelector(({ global }) => global.hrisStatusCounts)
  const hrisEligibility = useSelector(({ global }) => global.hrisEligibility)
  const hasAccount = useMemo(() => hrisEnabled ? !!account?.unique_id : false, [hrisEnabled, account?.unique_id])

  useEffect(() => {
    if (hrisEnabled && !hrisAccounts && isAdmin) {
      dispatch({ type: GET_HRIS_ACCOUNTS })
    }
  }, [hrisEnabled, typeof hrisAccounts, firstSyncStatus?.status])

  useEffect(() => {
    if (hasAccount && !status && isAdmin) {
      dispatch({
        type: GET_HRIS_STATUS,
        account_id: account.unique_id,
      })
    }
  }, [hasAccount])

  useEffect(() => {
    if (hrisEnabled && account?.unique_id && isEmpty(firstSyncStatus) && isAdmin) {
      dispatch({
        type: GET_FIRST_SYNC_STATUS,
        account_id: account.unique_id,
      })
    }
  }, [hrisEnabled, account?.unique_id])

  useEffect(() => {
    if (hrisEnabled && hasAccount && !!!hrisStatusCounts && isAdmin) {
      dispatch({ type: GET_HRIS_STATUS_COUNTS })
    }
  }, [hrisEnabled, hasAccount, !!!hrisStatusCounts])

  useEffect(() => {
    if (hrisEnabled && hasAccount && !!!hrisEligibility && isAdmin) {
      dispatch({ type: GET_HRIS_ELIGIBILITY })
    }
  }, [hrisEnabled, hasAccount, !!!hrisEligibility])

  return useMemo(() => ({
    account,
    status,
    firstSyncStatus,
    hasAccount,
    hrisStatusCounts: hrisStatusCounts || {
      error_count: 0,
      ineligible_count: 0,
      waiting_count: 0,
      existing_count: 0,
    },
    hrisEligibility: hrisEligibility || {
      teams: [],
      employment_types: [],
      hire: {},
      total_eligible_employees: 0,
      total_eligible_error_employees: 0,
    },
    hrisEnabled,
    isLoading: hrisAccounts === null,
  }), [
    account,
    status,
    firstSyncStatus,
    hasAccount,
    hrisStatusCounts,
    hrisEligibility,
    hrisEnabled,
    hrisAccounts === null,
  ])
}

const checkStatus = data => (data?.length || isNumber(data) ? data : undefined)

const STATUS = {
  '': {
    color: undefined,
    label: undefined,
    allowPlaid: false,
    label: 'Disabled until verified',
  },
  default: {
    color: undefined,
    label: undefined,
    allowPlaid: false,
    label: 'Disabled until verified',
  },

  // Dwolla savings_account_status
  [DWOLLA_STATUS.UNVERIFIED]: {
    color: 'warning',
    label: 'Verification in progress',
    allowPlaid: false,
    disabled: true,
  },
  [DWOLLA_STATUS.VERIFIED]: {
    color: 'success',
    label: "You're verified!",
    allowPlaid: true,
  },
  [DWOLLA_STATUS.RETRY]: {
    color: 'error',
    label: 'Your account requires attention',
    allowPlaid: false,
  },
  [DWOLLA_STATUS.DOCUMENT]: {
    color: 'error',
    label: 'Your account requires attention',
    allowPlaid: false,
  },
  [DWOLLA_STATUS.SUSPENDED]: {
    color: 'error',
    label: 'Your account requires attention',
    allowPlaid: false,
  },
  [DWOLLA_STATUS.DEACTIVATED]: {
    color: undefined,
    label: 'Verification in progress',
    allowPlaid: false,
  },
  [DWOLLA_STATUS.REACTIVATED]: {
    color: 'success',
    label: "You're verified!",
    allowPlaid: true,
  },
  [DWOLLA_STATUS.SUBMITTED]: {
    color: 'warning',
    label: 'Verification in progress',
    allowPlaid: false,
    disabled: true,
  },

  // Dwolla savings_account_sub_status
  [DWOLLA_SUB_STATUS.CREATED]: {
    color: 'success',
    label: "You're verified!",
    allowPlaid: true,
  },
  [DWOLLA_SUB_STATUS.REVERIFICATION_NEEDED]: {
    color: 'error',
    label: 'Your account requires attention',
    allowPlaid: false,
  },
  [DWOLLA_SUB_STATUS.VERIFICATION_DOCUMENT_NEEDED]: {
    color: 'error',
    label: 'Your account requires attention',
    allowPlaid: false,
  },
  [DWOLLA_SUB_STATUS.VERIFICATION_DOCUMENT_UPLOADED]: {
    color: 'warning',
    label: 'Verification in progress',
    allowPlaid: false,
    disabled: true,
  },
  [DWOLLA_SUB_STATUS.VERIFICATION_DOCUMENT_FAILED]: {
    color: 'error',
    label: 'Your account requires attention',
    allowPlaid: false,
  },
  [DWOLLA_SUB_STATUS.VERIFICATION_DOCUMENT_APPROVED]: {
    color: 'success',
    label: "You're verified!",
    allowPlaid: true,
  },
  [DWOLLA_SUB_STATUS.VERIFIED]: {
    color: 'success',
    label: "You're verified!",
    allowPlaid: true,
  },
  [DWOLLA_SUB_STATUS.SUSPENDED]: {
    color: 'error',
    label: 'Your account requires attention',
    allowPlaid: false,
  },
  [DWOLLA_SUB_STATUS.ACTIVATED]: {
    color: 'success',
    label: "You're verified!",
    allowPlaid: true,
  },
  [DWOLLA_SUB_STATUS.DEACTIVATED]: {
    color: undefined,
    label: 'Verification in progress',
    allowPlaid: false,
  },
  [DWOLLA_SUB_STATUS.VERIFY_WITH_DOCUMENT]: {
    color: 'error',
    label: 'Your account requires attention',
    allowPlaid: false,
  },
  [DWOLLA_SUB_STATUS.VERIFY_BUSINESS_WITH_DOCUMENT]: {
    color: 'error',
    label: 'Your account requires attention',
    allowPlaid: false,
  },
  [DWOLLA_SUB_STATUS.VERIFY_CONTROLLER_AND_BUSINESS_WITH_DOCUMENT]: {
    color: 'error',
    label: 'Your account requires attention',
    allowPlaid: false,
  },
  [DWOLLA_SUB_STATUS.DOCUMENTS_PRE_SUBMIT]: {
    color: 'warning',
    label: 'Verification in progress',
    allowPlaid: false,
    disabled: true,
  },
  [DWOLLA_SUB_STATUS.DOCUMENT_UPLOADING]: {
    color: 'warning',
    label: 'Verification in progress',
    allowPlaid: false,
    disabled: true,
  },

  // Dwolla beneficial_owners_certified
  RECERTIFY_BENEFICIAL_OWNERS: {
    color: 'error',
    label: 'Beneficial Owner requires attention',
    allowPlaid: false,
    disabled: true,
  },
  // 'RECERTIFY_BENEFICIAL_OWNERS': { color: 'error', label: "Transaction ability disabled pending Benefical Owner verification", allowPlaid: false, disabled: true },

  // Plaid error_status | verification_status
  [PLAID_STATUS.PENDING_AUTOMATIC_VERIFICATION]: {
    color: 'warning',
    label: 'Verification in progress',
    allowPlaid: false,
    disabled: true,
  },
  [PLAID_STATUS.AUTOMATICALLY_VERIFIED]: {
    color: 'success',
    label: "You're verified!",
    allowPlaid: true,
    disabled: true,
  },
  [PLAID_STATUS.PENDING_MANUAL_VERIFICATION]: {
    color: 'error',
    label: 'Your account requires attention',
    allowPlaid: true,
  },
  [PLAID_STATUS.MANUALLY_VERIFIED]: {
    color: 'success',
    label: "You're verified!",
    allowPlaid: true,
  },
  [PLAID_STATUS.VERIFICATION_EXPIRED]: {
    color: 'error',
    label: 'Your account requires attention',
    allowPlaid: true,
  },
  [PLAID_STATUS.DWOLLA_VERIFIED]: {
    color: 'success',
    label: "You're verified!",
    allowPlaid: true,
  },
}

export const useCorporateExternalAccount = () => {
  const dispatch = useDispatch()
  const { isAdmin } = useRoles()
  const corporateExternalAccounts = useSelector(({ global }) => global.corporateExternalAccounts)
  const { isLoading } = useTypeLoad(GET_CORPORATE_EXTERNAL_ACCOUNTS)
  const company = useSelector(({ global }) => global.company)
  const {
    savings_account_status,
    savings_account_sub_status,
    beneficial_owners_certified,
    has_savings_account,
    can_have_beneficial_owners,
    has_beneficial_owners,
  } = company || {}
  const [
    account,
    status,
    config,
  ] = useMemo(() => {
    const tempAccount = corporateExternalAccounts?.[0] || {}
    const tempStatus = 
      checkStatus(tempAccount.error_status) ||
      checkStatus(tempAccount.verification_status) ||
      (!beneficial_owners_certified &&
      has_beneficial_owners &&
      has_savings_account
        ? 'RECERTIFY_BENEFICIAL_OWNERS'
        : undefined) ||
      checkStatus(savings_account_sub_status) ||
      checkStatus(savings_account_status) ||
      ''

    return [
      tempAccount,
      tempStatus, 
      STATUS[tempStatus] || {},
    ]
  }, [
    corporateExternalAccounts,
    // account,
    savings_account_status,
    savings_account_sub_status,
    beneficial_owners_certified,
    has_savings_account,
    has_beneficial_owners,
  ])

  useEffect(() => {
    if (isEmpty(corporateExternalAccounts) && isAdmin) {
      dispatch({ type: GET_CORPORATE_EXTERNAL_ACCOUNTS })
    }
    if (isEmpty(company) && isAdmin) {
      dispatch({ type: GET_COMPANY })
    }
  }, [])

  return useMemo(() => ({
    primaryAccount: account,
    status,
    config,
    isLoading,
    hasAccount: !isEmpty(account),
  }), [
    status,
    isLoading,
    isEmpty(account),
  ])
}

export const usePayroll = data => {
  const {
    onInteraction,
    onFinish,
    onClose,
  } = data || {}
  const dispatch = useDispatch()
  const payrollAccessToken = useSelector(({ global }) => global.payrollAccessToken)
  const company = useGet(GET_COMPANY, 'company', null)
  const uniqueID = useUniqueID()

  const openAtomic = useCallback(() => {
    dispatch({
      type: GET_PAYROLL_ACCESS_TOKEN,
      uniqueID,
    })
  }, [uniqueID])

  const handleOnInteraction = useCallback(interaction => {
    onInteraction?.(interaction)
  }, [])

  const clearToken = useCallback(() => {
    dispatch(clearFromState(['payrollAccessToken']))
  }, [])

  const handleOnFinish = useCallback(data => {
    dispatch({ type: GET_CONTRIBUTION_SETTINGS })
    clearToken()
    onFinish?.(data)
  }, [])

  const handleOnClose = useCallback(data => {
    clearToken()
    onClose?.(data)
  }, [])

  useEffect(() => () => {
      dispatch(clearFromState(['payrollAccessToken']))
    }, [])

  useEffect(() => {
    if (payrollAccessToken?.public_token && !!company && uniqueID === payrollAccessToken?.uniqueID) {
      const config = {
        publicToken: payrollAccessToken.public_token,
        tasks: [{ product: Product.DEPOSIT }],
      }
      if (!!company.payroll_external_id) {
        config.deeplink = {
          step: 'login-company',
          companyId: company.payroll_external_id,
          companyName: company.name,
        }
      }
      Atomic.transact({
        config,
        onInteraction: handleOnInteraction,
        onFinish: handleOnFinish,
        onClose: handleOnClose,
      })
    }
  }, [payrollAccessToken?.public_token, !!company])

  return useMemo(() => ({ openAtomic }), [openAtomic])
}

export const useCorporateExternalCards = () => {
  const dispatch = useDispatch()
  const { openModal } = useAction()
  const corporateExternalCard = useSelector(({ global }) => global.corporateExternalCard)
  
  useEffect(() => {
    if (isEmpty(corporateExternalCard)) {
      dispatch({ type: GET_CORPORATE_EXTERNAL_CARD })
    }
  }, [])

  return {
    isLoading: corporateExternalCard === null,
    // primaryCard: null,
    // hasLinkedCard: false,
    primaryCard: corporateExternalCard?.unique_id ? corporateExternalCard : {},
    hasLinkedCard: !!corporateExternalCard?.unique_id,
    onRemoveCard: () => {
      openModal(
        <RemoveCorporateExternalCard 
          card={corporateExternalCard} 
          onRemove={() => {
            dispatch({ 
              type: REMOVE_CORPORATE_EXTERNAL_CARD,
              unique_id: corporateExternalCard?.unique_id,
            })
          }}
        />
      )
    },
  }
}

export const useExternalCards = () => {
  const dispatch = useDispatch()
  const externalCards = useSelector(({ global }) => global.externalCards)

  useEffect(() => {
    if (isEmpty(externalCards)) {
      dispatch({ type: GET_EXTERNAL_CARDS })
    }
  }, [])

  return useMemo(() => ({
    isLoading: externalCards === null,
    // primaryCard: null,
    // hasLinkedCard: false,
    primaryCard: externalCards?.[0],
    externalCards: externalCards || [],
    hasLinkedCard: !isEmpty(externalCards),
    onRemoveCard: unique_id => dispatch({ type: REMOVE_EXTERNAL_CARD, unique_id }),
  }), [externalCards])
}

// export const useCorporatePaymentSource = () => {
//   const dispatch = useDispatch()
//   const corporateExternalCard = useSelector(({ global }) => global.corporateExternalCard)

//   useEffect(() => {
//     if (isEmpty(corporateExternalCard)) {
//       dispatch({ type: GET_CORPORATE_EXTERNAL_CARD })
//     }
//   }, [])

//   return useMemo(() => ({
//     isLoading: corporateExternalCard === null,
//     // primaryCard: null,
//     // hasLinkedCard: false,
//     primaryCard: corporateExternalCard,
//     hasLinkedCard: !isEmpty(corporateExternalCard),
//   }), corporateExternalCard)
// }